import React, { useState, useEffect } from 'react';
import { Tabs, useTabState, usePanelState } from '../../../components/tabs';
import SignIn from './SignIn';
import Register from './Register';
import MessageBox from '../../../components/MessageBox';
import '../../../../sass/apps/contact/authentication.scss';

function loadRecaptcha(recaptcha_key) {
  return new Promise((resolve, reject) => {
    if (window.recaptcha) {
      resolve();
    }
    // empty callback to avoid recaptcha info logs about ready method not in use
    window.grecaptchaLoaded = () => grecaptcha.ready(() => null);
    const scriptTag = document.createElement('script');
    scriptTag.type = 'text/javascript';
    scriptTag.src = `https://www.google.com/recaptcha/api.js?onload=grecaptchaLoaded&render=${recaptcha_key}`;
    scriptTag.addEventListener('load', () => resolve());
    scriptTag.addEventListener('error', () =>
      reject(new Error('Recaptcha failed to load')),
    );
    document.getElementsByTagName('script')[0].append(scriptTag);
  });
}

function getErrorMessage(message, subDir) {
  if (message.includes('You have already registered with this email address')) {
    return (
      <div>
        You have already registered with this email address. Please either login
        or check the email address you have provided.
        <a
          href={`/${subDir}/passwordreminder.pl`}
          target="_password"
          title="Reset your password (opens in new window)"
        >
          {' '}
          {localisations.forgot_password}
        </a>
      </div>
    );
  }
  return message;
}

function Tab({ ariaLabel, children, disabled, panelId }) {
  const { onClick, isActive } = useTabState();
  const baseClass = 'tabbed-panel__tab';
  const activeClass = isActive ? ` ${baseClass}--selected` : '';
  const tabClass = baseClass + activeClass;

  return (
    <button
      aria-controls={panelId}
      aria-label={ariaLabel}
      aria-selected={isActive}
      className={tabClass}
      disabled={disabled}
      onClick={onClick}
      role="tab"
      type="button"
    >
      {children}
    </button>
  );
}

function Panel({ children, id }) {
  const isActive = usePanelState();

  return isActive ? (
    <div id={id} role="tabpanel" className="tabbed-panel__panel">
      {children}
    </div>
  ) : null;
}

export default function AuthenticationForm({
  advertType,
  affiliateId,
  contactMode,
  country,
  csrf_token,
  error,
  facebookAppLocale,
  facebookLoginActive,
  locationId,
  loginFromUrl,
  subDir,
  user,
  userKnown,
  userLanguage,
  recaptcha_v3_site_key,
  passwordInputRef,
  localisations,
  siteId
}) {
  const userType = advertType == 'offered' ? 'lookingforashare' : 'haveashare';
  const [logInFormFields, updateLogInFormFields] = useState({
    login_email: {
      value: user.login_email,
      valid: false,
    },
    login_password: {
      value: user.login_password,
      valid: false,
    },
    remember_me: {
      value: false,
    },
  });
  const [registerFormFields, updateRegisterFormFields] = useState({
    first_name: {
      value: user.newuser_first_name,
      valid: false,
    },
    last_name: {
      value: user.newuser_last_name,
      valid: false,
    },
    email: {
      value: user.newuser_email,
      valid: false,
    },
    email_again: {
      value: user.newuser_email_again,
      valid: false,
    },
    password: {
      value: user.password,
      valid: false,
    },
    gender: {
      value: '',
      valid: false,
    },
    where_heard: {
      value: '',
      valid: true,
    },
  });

  const [tabIndex, setTabIndex] = useState(0);
  const [passwordVisible, setPasswordVisible] = useState(false);

  // use setIndex as you want
  // for example force to second tab in initial render
  useEffect(() => {
    const index = userKnown && !user.newuser_email ? 1 : 0;
    setTabIndex(index);
  }, [user.newuser_email, userKnown]);

  useEffect(() => {
    loadRecaptcha(recaptcha_v3_site_key);
  }, [recaptcha_v3_site_key]);

  function updateLogInFields({ field_name, valid, value }) {
    return updateLogInFormFields({
      ...logInFormFields,
      [field_name]: {
        value,
        valid,
      },
    });
  }

  function updateRegisterFields({ field_name, valid, value }) {
    return updateRegisterFormFields({
      ...registerFormFields,
      [field_name]: {
        value,
        valid,
      },
    });
  }

  function togglePasswordVisibility() {
    setPasswordVisible(!passwordVisible);
  }

  return (
    <div className="authentication">
      <div className="tabbed-panel">
        <Tabs state={[tabIndex, setTabIndex]}>
          <div role="tablist" className="tabbed-panel__tabs">
            <Tab panelId="panel-1">{localisations.register}</Tab>
            <Tab panelId="panel-2">{localisations.log_in}</Tab>
          </div>
          <div className="tabbed-panel__panels">
            {error ? (
              <div className="authentication__message-box">
                <MessageBox type={'error'}>
                  {getErrorMessage(error, subDir)}
                </MessageBox>
              </div>
            ) : null}
            <Panel id="panel-1">
              <Register
                affiliateId={affiliateId}
                contactMode={contactMode}
                country={country}
                csrf_token={csrf_token}
                facebookAppLocale={facebookAppLocale}
                facebookLoginActive={facebookLoginActive}
                fields={registerFormFields}
                locationId={locationId}
                loginFromUrl={loginFromUrl}
                subDir={subDir}
                updateFields={updateRegisterFields}
                userLanguage={userLanguage}
                userType={userType}
                passwordInputRef={passwordInputRef}
                togglePasswordVisibility={togglePasswordVisibility}
                passwordVisible={passwordVisible}
                localisations={localisations}
                siteId={siteId}
              />
            </Panel>
            <Panel id="panel-2">
              <SignIn
                affiliateId={affiliateId}
                contactMode={contactMode}
                facebookAppLocale={facebookAppLocale}
                facebookLoginActive={facebookLoginActive}
                fields={logInFormFields}
                locationId={locationId}
                loginFromUrl={loginFromUrl}
                subDir={subDir}
                updateFields={updateLogInFields}
                userType={userType}
                localisations={localisations}
              />
            </Panel>
          </div>
        </Tabs>
      </div>
      <span
        style={tabIndex === 1 ? { display: 'none' } : { display: 'block' }}
        className="sr-only"
        aria-live="polite"
      >
        {passwordVisible ? localisations.your_password_is_shown : localisations.your_password_is_hidden}
      </span>
    </div>
  );
}
